import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  Row,
  Col,
  InputGroup,
  FormSelect,
  Image
} from 'react-bootstrap';
import {
  FaUserTie,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaSave,
  FaArrowLeft,
  FaCar,
  FaCalendarAlt,
  FaPalette,
  FaCarSide,
  FaTag,
  FaTrash
} from 'react-icons/fa';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const MAX_PHOTOS = 5;
const MAX_FILE_SIZE_MB = 5; // Maximum file size in MB

const AddEditDriver = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [driver, setDriver] = useState({
    name: '',
    phone: '',
    email: '',
    licenseNumber: '',
    isAvailable: true,
    vehiclePlateNumber: '',
    vehicleModelName: '',
    vehicleMakeYear: new Date().getFullYear(),
    vehicleColor: '',
    vehicleType: 'Sedan',
    vehicleBrandName: '',
    vehicleExteriorPhotos: [],
    vehicleInteriorPhotos: []
  });

  const [exteriorFiles, setExteriorFiles] = useState([]);
  const [interiorFiles, setInteriorFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoErrors, setPhotoErrors] = useState({ exterior: '', interior: '' });

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    axios
      .get(`${API}/admin/drivers/${id}`, { 
        headers: { 
          'x-auth-token': token,
          'Content-Type': 'application/json'
        } 
      })
      .then(res => setDriver(res.data))
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to load driver');
        toast.error(err.response?.data?.error || 'Failed to load driver');
      })
      .finally(() => setLoading(false));
  }, [isEdit, id, API, token]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setDriver(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return false;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return false;
    }
    return true;
  };

  const handlePhotoInput = (e, type) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validate all files first
    for (const file of files) {
      if (!validateFile(file)) {
        e.target.value = '';
        return;
      }
    }

    if (type === 'Exterior') {
      if ((driver.vehicleExteriorPhotos.length + exteriorFiles.length + files.length) > MAX_PHOTOS) {
        setPhotoErrors(prev => ({ ...prev, exterior: `Maximum ${MAX_PHOTOS} exterior photos allowed` }));
        toast.error(`Maximum ${MAX_PHOTOS} exterior photos allowed`);
        e.target.value = '';
        return;
      }
      setExteriorFiles(prev => [...prev, ...files]);
      setPhotoErrors(prev => ({ ...prev, exterior: '' }));
    } else {
      if ((driver.vehicleInteriorPhotos.length + interiorFiles.length + files.length) > MAX_PHOTOS) {
        setPhotoErrors(prev => ({ ...prev, interior: `Maximum ${MAX_PHOTOS} interior photos allowed` }));
        toast.error(`Maximum ${MAX_PHOTOS} interior photos allowed`);
        e.target.value = '';
        return;
      }
      setInteriorFiles(prev => [...prev, ...files]);
      setPhotoErrors(prev => ({ ...prev, interior: '' }));
    }
    e.target.value = '';
  };

  const removeLocalPhoto = (idx, type) => {
    if (type === 'Exterior') {
      setExteriorFiles(prev => prev.filter((_, i) => i !== idx));
    } else {
      setInteriorFiles(prev => prev.filter((_, i) => i !== idx));
    }
  };

  const removeServerPhoto = async (idx, type) => {
    const field = type === 'Exterior' ? 'vehicleExteriorPhotos' : 'vehicleInteriorPhotos';
    const url = driver[field][idx];
    if (!url) return;
    
    const old = [...driver[field]];
    setDriver(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== idx)
    }));
    
    try {
      await axios.delete(`${API}/admin/drivers/${id}/photo`, {
        headers: { 
          'x-auth-token': token,
          'Content-Type': 'application/json'
        },
        data: { url, type: field }
      });
      toast.success('Photo removed successfully');
    } catch (err) {
      setDriver(prev => ({
        ...prev,
        [field]: old
      }));
      toast.error(err.response?.data?.error || 'Failed to delete photo');
    }
  };

  const uploadPhotos = async (files, type) => {
    if (!files.length) return [];
    
    const uploadedUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('photo', file);
        
        const response = await axios.post(`${API}/admin/drivers/upload-photo`, formData, {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000 // 30 seconds timeout
        });
        
        if (response.data.url) {
          uploadedUrls.push(response.data.url);
        } else {
          throw new Error('No URL returned from server');
        }
      } catch (err) {
        console.error(`Error uploading ${type} photo:`, err);
        throw err; // Re-throw to be caught in handleSubmit
      }
    }
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      // Append text fields
      Object.entries({
        name: driver.name,
        phone: driver.phone,
        email: driver.email,
        licenseNumber: driver.licenseNumber,
        isAvailable: driver.isAvailable,
        vehiclePlateNumber: driver.vehiclePlateNumber,
        vehicleModelName: driver.vehicleModelName,
        vehicleMakeYear: driver.vehicleMakeYear,
        vehicleColor: driver.vehicleColor,
        vehicleType: driver.vehicleType,
        vehicleBrandName: driver.vehicleBrandName
      }).forEach(([key, value]) => formData.append(key, value));
  
      // Append file arrays
      exteriorFiles.forEach(file => formData.append('vehicleExteriorPhotos', file));
      interiorFiles.forEach(file => formData.append('vehicleInteriorPhotos', file));
  
      const config = { headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' } };
      if (isEdit) {
        await axios.put(`${API}/admin/drivers/${id}`, formData, config);
        toast.success('Driver updated successfully');
      } else {
        await axios.post(`${API}/admin/drivers`, formData, config);
        toast.success('Driver added successfully');
      }
      navigate('/admin/drivers');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="driver-form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEdit ? 'Edit Driver' : 'Add New Driver'}</h2>
        <Button variant="secondary" onClick={() => navigate('/admin/drivers')}>
          <FaArrowLeft className="me-2" /> Back to Drivers
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {/* Personal Info */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaUserTie /></InputGroup.Text>
                    <Form.Control
                      name="name"
                      value={driver.name}
                      onChange={handleChange}
                      placeholder="Driver's full name"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaPhone /></InputGroup.Text>
                    <Form.Control
                      name="phone"
                      type="tel"
                      value={driver.phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                    <Form.Control
                      name="email"
                      type="email"
                      value={driver.email}
                      onChange={handleChange}
                      placeholder="Email address"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>License Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaIdCard /></InputGroup.Text>
                    <Form.Control
                      name="licenseNumber"
                      value={driver.licenseNumber}
                      onChange={handleChange}
                      placeholder="License number"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Vehicle Info */}
            <h5 className="mt-4 mb-3"><FaCar className="me-2" /> Vehicle Information</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Plate Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaTag /></InputGroup.Text>
                    <Form.Control
                      name="vehiclePlateNumber"
                      value={driver.vehiclePlateNumber}
                      onChange={handleChange}
                      placeholder="Plate number"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Model Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaCarSide /></InputGroup.Text>
                    <Form.Control
                      name="vehicleModelName"
                      value={driver.vehicleModelName}
                      onChange={handleChange}
                      placeholder="Model name"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Make Year</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaCalendarAlt /></InputGroup.Text>
                    <Form.Control
                      name="vehicleMakeYear"
                      type="number"
                      min="2000"
                      max={new Date().getFullYear()}
                      value={driver.vehicleMakeYear}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Color</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaPalette /></InputGroup.Text>
                    <Form.Control
                      name="vehicleColor"
                      value={driver.vehicleColor}
                      onChange={handleChange}
                      placeholder="Color"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <FormSelect
                    name="vehicleType"
                    value={driver.vehicleType}
                    onChange={handleChange}
                    required
                  >
                    <option>Sedan</option>
                    <option>SUV</option>
                    <option>Van</option>
                    <option>Minibus</option>
                  </FormSelect>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaCarSide /></InputGroup.Text>
                    <Form.Control
                      name="vehicleBrandName"
                      value={driver.vehicleBrandName}
                      onChange={handleChange}
                      placeholder="Brand name"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Photos */}
            <h5 className="mt-4 mb-3"><FaCar className="me-2" /> Photos</h5>
            <Row>
              {/* Exterior Photos */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Exterior Photos ({driver.vehicleExteriorPhotos.length + exteriorFiles.length}/{MAX_PHOTOS})
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={driver.vehicleExteriorPhotos.length + exteriorFiles.length >= MAX_PHOTOS}
                    onChange={e => handlePhotoInput(e, 'Exterior')}
                  />
                  <Form.Text className="text-muted">
                    Max {MAX_FILE_SIZE_MB}MB per file, JPG/PNG recommended
                  </Form.Text>
                  {photoErrors.exterior && (
                    <Form.Text className="text-danger">
                      {photoErrors.exterior}
                    </Form.Text>
                  )}
                  <div className="d-flex flex-wrap mt-2">
                    {/* Existing uploaded photos */}
                    {driver.vehicleExteriorPhotos.map((url, idx) => (
                      <div key={idx} className="position-relative me-2 mb-2" style={{ width: 80, height: 80 }}>
                        <Image
                          src={url}
                          alt={`exterior-${idx}`}
                          thumbnail
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removeServerPhoto(idx, 'Exterior')}
                          style={{ transform: 'translate(50%, -50%)' }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                    {/* Local preview photos */}
                    {exteriorFiles.map((file, idx) => (
                      <div key={idx + 1000} className="position-relative me-2 mb-2" style={{ width: 80, height: 80 }}>
                        <Image
                          src={URL.createObjectURL(file)}
                          alt={`exterior-new-${idx}`}
                          thumbnail
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removeLocalPhoto(idx, 'Exterior')}
                          style={{ transform: 'translate(50%, -50%)' }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Form.Group>
              </Col>
              {/* Interior Photos */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Interior Photos ({driver.vehicleInteriorPhotos.length + interiorFiles.length}/{MAX_PHOTOS})
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={driver.vehicleInteriorPhotos.length + interiorFiles.length >= MAX_PHOTOS}
                    onChange={e => handlePhotoInput(e, 'Interior')}
                  />
                  <Form.Text className="text-muted">
                    Max {MAX_FILE_SIZE_MB}MB per file, JPG/PNG recommended
                  </Form.Text>
                  {photoErrors.interior && (
                    <Form.Text className="text-danger">
                      {photoErrors.interior}
                    </Form.Text>
                  )}
                  <div className="d-flex flex-wrap mt-2">
                    {/* Existing uploaded photos */}
                    {driver.vehicleInteriorPhotos.map((url, idx) => (
                      <div key={idx} className="position-relative me-2 mb-2" style={{ width: 80, height: 80 }}>
                        <Image
                          src={url}
                          alt={`interior-${idx}`}
                          thumbnail
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removeServerPhoto(idx, 'Interior')}
                          style={{ transform: 'translate(50%, -50%)' }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                    {/* Local preview photos */}
                    {interiorFiles.map((file, idx) => (
                      <div key={idx + 1000} className="position-relative me-2 mb-2" style={{ width: 80, height: 80 }}>
                        <Image
                          src={URL.createObjectURL(file)}
                          alt={`interior-new-${idx}`}
                          thumbnail
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removeLocalPhoto(idx, 'Interior')}
                          style={{ transform: 'translate(50%, -50%)' }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isAvailable"
                label="Driver is currently available"
                checked={driver.isAvailable}
                onChange={handleChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    {isEdit ? 'Update Driver' : 'Add Driver'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddEditDriver;